import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { TFunction, Trans, useTranslation } from '@frontend/i18n';
import { SURCHARGE_MIN_VALUE, MaxSurchargeInfoRecord } from '@frontend/payments-hooks';
import { theme } from '@frontend/theme';
import {
  CheckboxField,
  Heading,
  Modal,
  ModalControlModalProps,
  NumberField,
  Text,
  TextLink,
  useFormField,
  ValidatorFieldState,
  ValidatorFn,
} from '@frontend/design-system';
import { useDebouncedEffect } from '../../hooks/utils/use-debounced-effect';
import { SettingsToggle, SettingsToggleProps } from '../Settings/atoms/settings-toggle';
import * as styles from '../Settings/styles/payment-settings-page.styles';

export type SurchargeSettingsToggleProps = {
  surchargeEnabledLocations: string[];
  surchargeSwitchProps: SettingsToggleProps['switchProps'];
  surchargeModalProps: ModalControlModalProps;
  percentValue?: number;
  onChangePercentValue: (value: number) => void;
  onConfirmSurchargeModal: () => Promise<void>;
  combinedMaxSurchargeInfo: MaxSurchargeInfoRecord;
  hasPermission: boolean;
  disabled?: boolean;
};

const customStyles = {
  overrideSwitchStyles: (isInputVisible = false) => css`
    label {
      display: flex;
      ${isInputVisible ? `align-items: flex-start;` : 'align-items: center;'}
      gap: ${theme.spacing(1)};
    }
    ${isInputVisible && `button { margin-top: -${theme.spacing(2)}; }`}
  `,
  percentMessage: css`
    margin-top: ${theme.spacing(0.5)};
  `,
  percentageList: css`
    padding-left: ${theme.spacing(3)};
  `,
};

type PercentValidator = (
  maxPercentageDisplayValue: number,
  locationState: string | undefined,
  t: TFunction<'payments', undefined>
) => ValidatorFn<'number'>;

const percentValidator: PercentValidator =
  (maxPercentageDisplayValue, locationState, t) => (state: ValidatorFieldState<'number'>) => {
    const { value, error } = state;
    const numValue = parseFloat(value);
    const maxLimitMessage = locationState
      ? t('{{state}} state law limits surcharging to {{max}}%', {
          state: locationState,
          max: maxPercentageDisplayValue,
        })
      : t('State law of some of the selected locations limits surcharging to {{max}}%', {
          max: maxPercentageDisplayValue,
        });
    const defaultErrorMessage = numValue < SURCHARGE_MIN_VALUE / 100 ? error : '';
    return numValue > maxPercentageDisplayValue ? maxLimitMessage : defaultErrorMessage;
  };

export const SurchargeSettingsToggle = ({
  surchargeEnabledLocations,
  surchargeSwitchProps,
  surchargeModalProps,
  percentValue,
  onChangePercentValue,
  onConfirmSurchargeModal,
  combinedMaxSurchargeInfo,
  hasPermission,
  disabled = false,
}: SurchargeSettingsToggleProps) => {
  const { t } = useTranslation('payments');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { maximumSurchargePercentage, state: locationState } = combinedMaxSurchargeInfo;
  const maxPercentageDisplayValue = maximumSurchargePercentage / 100;
  const agreeField = useFormField({ type: 'checkbox', value: false });

  const percentField = useFormField(
    {
      value: String(((percentValue || 0) / 100).toFixed(2)),
      type: 'number',
      required: true,
      min: SURCHARGE_MIN_VALUE / 100,
      validator: percentValidator(maxPercentageDisplayValue, locationState, t),
    },
    [percentValue]
  );
  const percentFieldValue = percentField.value ? parseFloat(percentField.value) : 0;

  const heading = useMemo(() => {
    return surchargeSwitchProps.value ? t('Add Surcharge Fee') : t('Add Surcharge to Payment Requests');
  }, [surchargeSwitchProps.value]);

  useDebouncedEffect(
    () => {
      if (percentField.touched && percentFieldValue && !percentField.error) {
        if (isNaN(percentFieldValue)) {
          onChangePercentValue(0);
          return;
        }
        onChangePercentValue(percentFieldValue * 100);
      }
    },
    [percentFieldValue, percentField.touched, percentField.error],
    { delay: 750, runOnFirstRender: false }
  );

  const onPreventClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirmSurchargeModal();
    } catch (err) {
      console.error('Error confirming surcharge modal', err);
    }
    setIsSubmitting(false);
  };

  const disableLabelClick = surchargeSwitchProps.value;
  const labelContent = !surchargeSwitchProps.value ? (
    t('A percentage-based fee to credit card transactions to offset processing fees.')
  ) : (
    <div
      className='surcharge-settings-toggle__label-content'
      onClick={onPreventClick}
      css={{
        maxWidth: theme.spacing(40),
        width: '100%',
      }}
    >
      <NumberField
        css={{ label: { width: 'fit-content' } }}
        label={t('Percent')}
        name='surchargeAmount'
        {...percentField}
        disabled={disabled}
      />
      {!disabled && (
        <Text size='small' color='light' css={customStyles.percentMessage}>
          {t('You can choose a percentage up to {{max}}%', {
            max: maxPercentageDisplayValue,
          })}
        </Text>
      )}
    </div>
  );

  useEffect(() => {
    /**
     * Uncheck the agree box when modal is hidden.
     * Is closed from outside, so wrapping the modalProps.onClose here doesn't do it.
     */
    if (!surchargeModalProps.show) {
      agreeField.onChange({ name: 'agree', value: false });
    }
  }, [surchargeModalProps.show]);

  return (
    <>
      <Heading data-testid='surcharge-settings-toggle-heading' css={styles.sectionHeading} level={3}>
        {heading}
      </Heading>
      <SettingsToggle
        css={customStyles.overrideSwitchStyles(disableLabelClick)}
        name='surcharge'
        label={labelContent}
        disableLabelClick={disableLabelClick}
        infoText={t(
          'Surcharging is capped at {{max}}% and is available in all states except Connecticut, Maine, and Massachusetts.',
          { max: maxPercentageDisplayValue }
        )}
        locationIds={surchargeEnabledLocations}
        switchProps={surchargeSwitchProps}
        /**
         * We are moving away from "billing manager" roles for more fine-grained ACLs.
         * These haven't been created yet, so we're just putting it before FF.
         */
        hasPermission={hasPermission}
        permissionRoleLabel='"Manage Surcharging"'
        disabled={surchargeEnabledLocations.length === 0 || disabled}
        showChipForNoEnabledLocations={false}
      />

      <Modal.Responsive {...surchargeModalProps} title={t('Agree to Terms')} maxWidth={parseInt(theme.spacing(76))}>
        <Modal.Header>{t('Agree to Terms')}</Modal.Header>
        <Modal.Body>
          <Trans t={t}>
            <Text size='large'>
              Certain rules and regulations may prohibit the use of this feature in your area. Weave cannot provide you
              with information on whether surcharging is permissible in your area. It is crucial that you review and
              understand all applicable card brand rules (e.g., Visa, Mastercard, American Express, Discover) regarding
              surcharging before implementing this feature. Learn more{' '}
              <TextLink
                target='_blank'
                href='https://www.weavehelp.com/weavehelp/article/payment-request-adding-a-surcharge'
              >
                here
              </TextLink>
              .
            </Text>
            <Text size='large'>
              By enabling this feature, you are responsible for determining whether surcharging is legal in your area
              and what requirements are needed when adding surcharges to a customer’s bill. You understand that Weave
              will not be held legally or financially liable for any surcharge amounts charged while using this feature.
            </Text>
            <div>
              <Text size='large'>
                {/* in the name of expediency, hard coding 50 basis points here is probably ok for now, but this rate is negotiable by locations. */}
                Please note that enabling this feature will result in a 50 basis point (0.50%) increase ONLY on credit
                card processing related activities.
              </Text>
            </div>
            <div css={styles.marginBottom}>
              <CheckboxField {...agreeField} name='agree' label={t('I agree')} />
            </div>
          </Trans>
        </Modal.Body>

        <Modal.Footer
          primary={{
            label: t('Activate Surcharge Fee'),
            disabled: !agreeField.value,
            onClick: onConfirm,
            loading: isSubmitting,
            trackingId: 'settings--payments--surcharge--activate-btn',
          }}
          tertiary={{
            onClick: surchargeModalProps.onClose,
            label: t('Cancel'),
            disabled: isSubmitting,
            trackingId: 'settings--payments--surcharge--cancel-btn',
          }}
        />
      </Modal.Responsive>
    </>
  );
};
